/**
 * AccessDenied Component (403 Page)
 * Coláiste Lurgan Management System - Professional access denied page
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldX, Home, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

export function AccessDenied({
  requiredPermissions = [],
  requiredUserTypes = [],
  userType = null
}) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-lg w-full">
        <CardContent className="text-center py-8 px-6">
          {/* Access Denied Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-destructive/10 rounded-full">
              <ShieldX className="h-12 w-12 text-destructive" />
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-2xl font-semibold mb-2">
            {t('accessDenied.title')}
          </h1>

          <p className="text-muted-foreground mb-6">
            {t('accessDenied.message')}
          </p>

          {/* Required Permissions/User Types */}
          {(requiredPermissions.length > 0 || requiredUserTypes.length > 0) && (
            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              {requiredUserTypes.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium mb-2">
                    {t('accessDenied.userTypeRequired')}:
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {requiredUserTypes.map((type) => (
                      <Badge key={type} variant="secondary">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {requiredPermissions.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">
                    {t('accessDenied.permissionsRequired')}:
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {requiredPermissions.map((permission) => (
                      <Badge key={permission} variant="outline">
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {userType && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-sm text-muted-foreground flex items-center justify-center gap-2 flex-wrap">
                    {t('accessDenied.currentRole')}
                    <Badge variant="default">{userType}</Badge>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Button asChild className="flex items-center gap-2">
              <Link to="/dashboard">
                <Home className="h-4 w-4" />
                {t('accessDenied.backHome')}
              </Link>
            </Button>

            <Button variant="outline" onClick={() => window.history.back()} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('accessDenied.goBack')}
            </Button>

            <Button variant="outline" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {t('accessDenied.contactAdmin')}
            </Button>
          </div>

          {/* Coláiste Lurgan Branding */}
          <div className="pt-6 border-t text-sm text-muted-foreground">
            {t('notFound.systemName')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AccessDenied;